import { Injectable, OnDestroy } from '@angular/core';
import { CustomCart } from '@cart/const';
import { ActiveCartService } from '@cart/data-access';
import { ActiveModes, OccCustomerConsignment } from '@checkout/const';
import {
  CheckoutActions,
  CheckoutSelectors,
  DeliveryAddressFacade,
  PLACED_ORDER_PROCESS_ID,
  ReceiptTypeFacade,
  StateWithCustomCheckout,
} from '@checkout/data-access';
import { select, Store } from '@ngrx/store';
import { CustomOrder } from '@order/const';
import { ProcessSelectors, StateWithProcess } from '@spartacus/core';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { AdditionalOptionsService } from './additional-options.service';
import { ApplyingBonusService } from './applying-bonus.service';
import { PickupService } from './pickup.service';
import { TimeSlotsService } from './time-slots.service';

@Injectable({
  providedIn: 'root',
})
export class CustomCheckoutService implements OnDestroy {
  // Богомерзкий костыль против лишних редиректов из чекаута, если корзина пустая
  // корзина очищается после отработки placeOrder, потому на эффект PLACE_ORDER флаг ставится в true
  // и убирается в false НЕПОСРЕДСТВЕННО перед редиректом на order-confirmation
  // таким образом, хотя корзина удаляется, редирект не успевает произойти
  // ЛЮБЛЮ ХАЙБРИС
  waitForRedirectToOrderConfirmationPage$ = new BehaviorSubject<boolean>(false);

  private subscription = new Subscription();

  constructor(
    private additionalOptionsService: AdditionalOptionsService,
    private deliveryAddressFacade: DeliveryAddressFacade,
    private receiptTypeFacade: ReceiptTypeFacade,
    private timeSlotsService: TimeSlotsService,
    private pickupService: PickupService,
    private applyingBonusService: ApplyingBonusService,
    private store: Store<StateWithCustomCheckout>,
    private processStateStore: Store<StateWithProcess<void>>,
    private activeCartService: ActiveCartService,
  ) {}

  placeOrder(termsChecked: boolean): void {
    this.waitForRedirectToOrderConfirmationPage$.next(true);

    if (this.actionAllowed()) {
      let cartId;
      this.activeCartService
        .getActiveCartId()
        .subscribe((activeCartId) => (cartId = activeCartId))
        .unsubscribe();

      if (cartId) {
        this.store.dispatch(
          new CheckoutActions.PlaceOrder({
            cartId,
            termsChecked,
          }),
        );
      }
    }
  }

  resetDeliveryFeatures(): void {
    this.store.dispatch(new CheckoutActions.ResetDeliveryFeatures());
  }

  resetCheckout(): void {
    this.store.dispatch(new CheckoutActions.ResetCheckout());
  }

  getRedirectedToPaymentPage(): Observable<boolean> {
    return this.store.pipe(select(CheckoutSelectors.getRedirectedToPaymentPage));
  }

  setRedirectedToPaymentPage(redirected: boolean): void {
    this.store.dispatch(new CheckoutActions.SetRedirectedToPaymentPage(redirected));
  }

  getOrderDetails(): Observable<CustomOrder> {
    return this.store.pipe(select(CheckoutSelectors.getOrderDetails));
  }

  setOrderDetails(orderDetails: CustomOrder): void {
    return this.store.dispatch(new CheckoutActions.SetOrderDetails(orderDetails));
  }

  getPaymentMode(): Observable<string | undefined> {
    return this.store.pipe(select(CheckoutSelectors.getActivePaymentCode));
  }

  setPaymentMode(paymentMode: string): void {
    return this.store.dispatch(new CheckoutActions.SetPayment(paymentMode));
  }

  getReceiptType(): Observable<string> {
    return this.store.pipe(select(CheckoutSelectors.getActiveReceiptType));
  }

  setReceiptType(type: string): void {
    return this.store.dispatch(new CheckoutActions.SetReceiptType(type));
  }

  updateReceiptType(type: string): void {
    this.getReceiptType()
      .pipe(
        take(1),
        filter((currnetType) => currnetType !== type),
      )
      .subscribe(() => {
        this.store.dispatch(new CheckoutActions.SetReceiptType(type));
      });
  }

  getActiveModes(): Observable<ActiveModes> {
    return this.store.pipe(select(CheckoutSelectors.getActiveModes));
  }

  updateCustomerConsignment(cart: CustomCart): void {
    this.subscription.add(
      combineLatest({
        // deliveryModeId - обязательное поле, поэтому фильтруем пустые значения
        deliveryModeId: this.receiptTypeFacade.getActiveReceiptTypeId().pipe(filter(Boolean)),
        activeAddressId: this.deliveryAddressFacade.getActiveAddressId(),
        currentTimeSlot: this.timeSlotsService.getCurrentTimeSlot(),
        activeOptions: this.additionalOptionsService.getActiveOptions(),
        activeShop: this.pickupService.activeShop$,
        applyingBonus: this.applyingBonusService.getApplyingBonus(),
      })
        .pipe(
          map(({ deliveryModeId, activeAddressId, currentTimeSlot, activeOptions, activeShop, applyingBonus }) => ({
            deliveryModeId,
            deliveryAddressId: activeAddressId,
            deliveryTimeSlot: currentTimeSlot,
            lift: { hasLift: activeOptions?.hasLift, liftType: activeOptions?.liftType },
            services: activeOptions?.services,
            posName: activeShop?.name,
            pickupDate: activeShop?.pickupDate,
            bonusApplyingAmount:
              applyingBonus === undefined ? cart.bonus?.applyingAmountInfo?.value || 0 : applyingBonus,
          })),
        )
        .subscribe((payload) => {
          this.store.dispatch(new CheckoutActions.UpdateCustomerConsignment(payload));
        }),
    );
  }

  getCustomerConsignment(): Observable<OccCustomerConsignment> {
    return this.store.select(CheckoutSelectors.getCustomerConsignmentSuccess).pipe(
      filter((success) => Boolean(success)),
      switchMap(() => this.store.select(CheckoutSelectors.getCustomerConsignmentValue)),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  protected actionAllowed(): boolean {
    return true;
  }

  /**
   * Returns the place or schedule replenishment order's error flag
   */
  getPlaceOrderError(): Observable<boolean> {
    return this.processStateStore.pipe(select(ProcessSelectors.getProcessErrorFactory(PLACED_ORDER_PROCESS_ID)));
  }

  /**
   * Resets the place or schedule replenishment order's processing state
   */
  clearPlaceOrderState(): void {
    this.store.dispatch(new CheckoutActions.ClearPlaceOrder());
  }
}
